import { computed, reactive, readonly } from "vue";
import type { RouteLocationRaw } from "vue-router";

/**
 * Servicio centralizado de notificaciones tipo toast/snackbar + campanita.
 *
 * Reemplaza las llamadas sueltas a Swal.fire({ icon: "success" | "error" | "warning" | "info", ... })
 * repartidas por los componentes con un único punto de entrada, reutilizable
 * tanto en componentes con Composition API (setup()) como en helpers/servicios .ts.
 *
 * Además de mostrar un toast flotante (ver NotificationBar.vue), cada notificación
 * queda guardada en un historial independiente (ver history/unreadCount más abajo),
 * que alimenta la campanita del Topbar (NotificationCenterMenu.vue) para poder
 * revisar notificaciones aunque el toast ya se haya auto-cerrado.
 *
 * Uso típico dentro de un componente:
 *   import { useNotificationBar } from "@/core/composables/useNotificationBar";
 *   const notificationBar = useNotificationBar();
 *   notificationBar.success("Los cambios se guardaron correctamente");
 *   notificationBar.error("No se pudo completar la operación", { title: "Error" });
 */

export type NotificationBarType = "success" | "error" | "warning" | "info";

/** Acción clickeable opcional (ej. "Complete profile" -> navega a /edit-profile). */
export interface NotificationBarAction {
  label: string;
  to: RouteLocationRaw;
}

export interface NotificationBarItem {
  id: number;
  type: NotificationBarType;
  title?: string;
  message: string;
  /** Milisegundos antes de auto-cerrarse el toast flotante. 0 = no se cierra sola. */
  duration: number;
  /** Timestamp (Date.now()) de creación, usado para mostrar "hace X minutos" en la campanita. */
  createdAt: number;
  /** Si ya fue marcada como leída desde la campanita. No afecta al toast flotante. */
  read: boolean;
  /** Botón/link opcional para navegar a la sección relacionada (ver NotificationBar.vue / NotificationCenterMenu.vue). */
  action?: NotificationBarAction;
}

export interface NotificationBarOptions {
  title?: string;
  duration?: number;
  action?: NotificationBarAction;
  /**
   * Si es true, el toast se muestra y se auto-cierra como siempre pero
   * NO queda guardado en el historial (campanita). Usar para
   * confirmaciones puramente locales de la propia accion de quien lo
   * dispara (ej. "tu envio se guardo" en un panel de admin) — no son
   * notificaciones dirigidas a otro usuario ni algo que valga la pena
   * revisar despues, y no deberian sobrevivir a un cambio de sesion.
   */
  skipHistory?: boolean;
}

// Duración por defecto de un toast antes de auto-cerrarse.
const DEFAULT_DURATION = 6000;
// Máximo de toasts visibles simultáneamente; el más antiguo se descarta al superarlo.
const MAX_VISIBLE_NOTIFICATIONS = 5;
// Máximo de notificaciones que se conservan en el historial de la campanita.
const MAX_HISTORY = 20;

let nextId = 1;
const dismissTimers = new Map<number, ReturnType<typeof setTimeout>>();

// Toasts flotantes actualmente visibles (NotificationBar.vue).
const state = reactive<{ items: NotificationBarItem[] }>({ items: [] });
// Historial persistente para la campanita (NotificationCenterMenu.vue); no se
// auto-elimina cuando el toast flotante se cierra.
const historyState = reactive<{ items: NotificationBarItem[] }>({ items: [] });

function clearDismissTimer(id: number): void {
  const timer = dismissTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    dismissTimers.delete(id);
  }
}

function dismissNotification(id: number): void {
  clearDismissTimer(id);
  const index = state.items.findIndex((item) => item.id === id);
  if (index !== -1) {
    state.items.splice(index, 1);
  }
}

function scheduleAutoDismiss(item: NotificationBarItem): void {
  if (item.duration <= 0) {
    return;
  }
  const timer = setTimeout(() => dismissNotification(item.id), item.duration);
  dismissTimers.set(item.id, timer);
}

/** Pausa el auto-cierre (por ejemplo, cuando el mouse está sobre el toast). */
function pauseAutoDismiss(id: number): void {
  clearDismissTimer(id);
}

/** Reanuda el auto-cierre desde cero (por ejemplo, al quitar el mouse del toast). */
function resumeAutoDismiss(id: number): void {
  const item = state.items.find((notification) => notification.id === id);
  if (item) {
    scheduleAutoDismiss(item);
  }
}

function pushNotification(
  type: NotificationBarType,
  message: string,
  options: NotificationBarOptions = {},
): number {
  const id = nextId++;
  const item: NotificationBarItem = {
    id,
    type,
    message,
    title: options.title,
    duration: options.duration ?? DEFAULT_DURATION,
    createdAt: Date.now(),
    read: false,
    action: options.action,
  };

  state.items.push(item);

  if (state.items.length > MAX_VISIBLE_NOTIFICATIONS) {
    const oldest = state.items.shift();
    if (oldest) {
      clearDismissTimer(oldest.id);
    }
  }

  scheduleAutoDismiss(item);

  // El historial es independiente del toast flotante: se queda ahí aunque
  // el toast ya se haya auto-cerrado, hasta que el usuario lo marque como
  // leído — salvo que se pida skipHistory (ver NotificationBarOptions).
  if (!options.skipHistory) {
    historyState.items.unshift(item);
    if (historyState.items.length > MAX_HISTORY) {
      historyState.items.length = MAX_HISTORY;
    }
  }

  return id;
}

function clearAllNotifications(): void {
  state.items.forEach((item) => clearDismissTimer(item.id));
  state.items.splice(0, state.items.length);
}

function markAsRead(id: number): void {
  const item = historyState.items.find(
    (notification) => notification.id === id,
  );
  if (item) {
    item.read = true;
  }
}

function markAllAsRead(): void {
  historyState.items.forEach((item) => {
    item.read = true;
  });
}

function clearHistory(): void {
  historyState.items.splice(0, historyState.items.length);
}

/** Elimina una única notificación del historial (campanita), por id. No afecta a los toasts flotantes activos (ver dismiss). */
function removeFromHistory(id: number): void {
  const index = historyState.items.findIndex((item) => item.id === id);
  if (index !== -1) {
    historyState.items.splice(index, 1);
  }
}

const unreadCount = computed(
  () => historyState.items.filter((item) => !item.read).length,
);

export function useNotificationBar() {
  return {
    /** Lista reactiva de solo lectura, usada por NotificationBar.vue para pintar los toasts flotantes. */
    notifications: readonly(state.items),
    /** Historial reactivo de solo lectura, usado por NotificationCenterMenu.vue (la campanita). */
    history: readonly(historyState.items),
    /** Computed con la cantidad de notificaciones no leídas del historial. */
    unreadCount,
    /** API genérica: notify("success", "mensaje", { title: "Opcional" }) */
    notify: pushNotification,
    success: (message: string, options?: NotificationBarOptions) =>
      pushNotification("success", message, options),
    error: (message: string, options?: NotificationBarOptions) =>
      pushNotification("error", message, options),
    warning: (message: string, options?: NotificationBarOptions) =>
      pushNotification("warning", message, options),
    info: (message: string, options?: NotificationBarOptions) =>
      pushNotification("info", message, options),
    dismiss: dismissNotification,
    pause: pauseAutoDismiss,
    resume: resumeAutoDismiss,
    clear: clearAllNotifications,
    markAsRead,
    markAllAsRead,
    clearHistory,
    removeFromHistory,
  };
}
